import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { 
  Megaphone, 
  Tag, 
  Trophy, 
  ShirtIcon, 
  Users, 
  Newspaper, 
  Star, 
  AlertTriangle, 
  MessageCircle, 
  HelpCircle, 
  WifiOff, 
  Flame, 
  Info, 
  RefreshCcw 
} from "lucide-react";

const ADMIN_CATEGORIES = [
  { value: "Discount Code", icon: Tag, color: "bg-gradient-to-r from-green-500 to-emerald-600" },
  { value: "Point Opportunities", icon: Trophy, color: "bg-gradient-to-r from-yellow-500 to-orange-600" },
  { value: "Merchandise", icon: ShirtIcon, color: "bg-gradient-to-r from-purple-500 to-violet-600" },
  { value: "Lockeroom Updates", icon: Users, color: "bg-gradient-to-r from-blue-500 to-indigo-600" },
  { value: "News", icon: Newspaper, color: "bg-gradient-to-r from-cyan-500 to-teal-600" },
  { value: "SpotLight", icon: Star, color: "bg-gradient-to-r from-yellow-400 to-amber-500" },
  { value: "Alert", icon: AlertTriangle, color: "bg-gradient-to-r from-red-500 to-rose-600" },
  { value: "FeedBack", icon: MessageCircle, color: "bg-gradient-to-r from-pink-500 to-rose-500" },
  { value: "FAQ", icon: HelpCircle, color: "bg-gradient-to-r from-indigo-500 to-purple-600" },
  { value: "Outage", icon: WifiOff, color: "bg-gradient-to-r from-gray-600 to-slate-700" },
  { value: "Fire", icon: Flame, color: "bg-gradient-to-r from-red-600 to-orange-700" },
  { value: "Need to Know", icon: Info, color: "bg-gradient-to-r from-blue-600 to-cyan-600" },
  { value: "Player Loop", icon: RefreshCcw, color: "bg-gradient-to-r from-emerald-500 to-green-600" }
];

interface AdminPostToFeedProps {
  onPostCreated?: () => void;
}

export const AdminPostToFeed = ({ onPostCreated }: AdminPostToFeedProps) => {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [howToGetInfo, setHowToGetInfo] = useState("");
  const [priority, setPriority] = useState("1");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const selectedCategoryData = ADMIN_CATEGORIES.find(cat => cat.value === selectedCategory);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCategory || !title.trim()) {
      toast({
        title: "Missing Information",
        description: "Please select a category and enter a title.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('admin_feed_posts')
        .insert({
          category: selectedCategory,
          title: title.trim(),
          description: description.trim() || null,
          how_to_get_info: howToGetInfo.trim() || null,
          priority: parseInt(priority),
          admin_id: (await supabase.auth.getUser()).data.user?.id
        });

      if (error) throw error;

      toast({
        title: "Post Created",
        description: "Your admin announcement has been posted to the feed.",
      });

      // Reset form
      setSelectedCategory("");
      setTitle("");
      setDescription("");
      setHowToGetInfo("");
      setPriority("1");
      
      onPostCreated?.();

    } catch (error) {
      console.error('Error creating admin post:', error);
      toast({
        title: "Error",
        description: "Failed to create admin post. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-primary" />
          Admin Post to Feed
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category Selection */}
          <div className="space-y-3">
            <Label htmlFor="category">Select Category</Label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a category..." />
              </SelectTrigger>
              <SelectContent>
                {ADMIN_CATEGORIES.map((category) => {
                  const IconComponent = category.icon;
                  return (
                    <SelectItem key={category.value} value={category.value}>
                      <div className="flex items-center gap-2">
                        <IconComponent className="h-4 w-4" />
                        {category.value}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            {selectedCategoryData && (
              <Badge className={`${selectedCategoryData.color} text-white`}>
                <selectedCategoryData.icon className="h-3 w-3 mr-1" />
                {selectedCategoryData.value}
              </Badge>
            )}
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter announcement title..."
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide details about this announcement..."
              rows={4}
              maxLength={500}
            />
          </div>

          {/* How to Get Info */}
          <div className="space-y-2">
            <Label htmlFor="howToGetInfo">How to Get It / Instructions</Label>
            <Textarea
              id="howToGetInfo"
              value={howToGetInfo}
              onChange={(e) => setHowToGetInfo(e.target.value)}
              placeholder="Explain how users can access this, steps to take, etc..."
              rows={3}
              maxLength={500}
            />
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label htmlFor="priority">Priority Level</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Normal</SelectItem>
                <SelectItem value="2">High</SelectItem>
                <SelectItem value="3">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-primary to-primary-foreground"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Posting..." : "Post to Feed"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};